/**
 * Choosing what somebody's job is.
 *
 * Roles the person doing the choosing cannot give away are shown, disabled,
 * with the reason. Hiding them would leave a manager wondering where the option
 * went and asking somebody; showing them answers the question without anyone
 * having to ask it.
 *
 * Each card also opens to say, in plain words, what the role can actually do —
 * "27 things they can do" tells you a number, not whether you are about to hand
 * a new waiter the keys to the money. The list is folded away because most of
 * the time the name is enough; it is there for the moment it is not.
 */
import { useState } from 'react';

import { readableRole } from './staff.js';
import { describeRole } from './capabilities.js';

export function RolePicker({ roles, chosen, onChange, disabled }) {
  const [open, setOpen] = useState(null);

  function toggle(id) {
    onChange(chosen.includes(id) ? chosen.filter((r) => r !== id) : [...chosen, id]);
  }

  return (
    <fieldset disabled={disabled}>
      <legend className="mb-1.5 text-sm font-medium text-ink-2">What is their job?</legend>

      <div className="grid gap-1.5 sm:grid-cols-2">
        {roles.map((role) => {
          const picked = chosen.includes(role.id);
          const blocked = !role.grantable;
          const expanded = open === role.id;

          return (
            <div
              key={role.id}
              className={`rounded-lg border transition-colors ${
                picked
                  ? 'border-brand-400 bg-brand-400/15'
                  : blocked
                    ? 'border-transparent bg-surface-3 opacity-60'
                    : 'border-rule'
              }`}
            >
              <button
                type="button"
                aria-pressed={picked}
                disabled={blocked}
                onClick={() => toggle(role.id)}
                className="block w-full p-2.5 text-left disabled:cursor-not-allowed"
              >
                <span
                  className={`block text-sm font-medium ${picked ? 'text-brand-300' : 'text-ink'}`}
                >
                  {readableRole(role.name)}
                </span>
                <span className="block text-xs text-ink-3">
                  {blocked ? 'Not yours to give' : `${role.permission_count} things they can do`}
                </span>
              </button>

              {/* Inspecting is separate from choosing — you can open any role's
                  list, even one you may not grant, to see what it would give. */}
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : role.id)}
                className="flex w-full items-center gap-1 border-t border-rule/60 px-2.5 py-1.5 text-left text-xs text-ink-3 hover:text-ink"
              >
                <span
                  aria-hidden="true"
                  className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
                >
                  &rsaquo;
                </span>
                {expanded ? 'Hide what they can do' : 'What they can do'}
              </button>

              {expanded && <RoleDetail permissions={role.permissions} />}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

/** The role's permissions, grouped by area and said in plain words. */
function RoleDetail({ permissions }) {
  const { everything, areas } = describeRole(permissions ?? []);

  if (everything) {
    return <p className="px-2.5 pb-2.5 text-xs text-ink-2">Full access — can do everything.</p>;
  }
  if (areas.length === 0) {
    return <p className="px-2.5 pb-2.5 text-xs text-ink-3">Nothing yet.</p>;
  }

  return (
    <ul className="space-y-1 px-2.5 pb-2.5 text-xs">
      {areas.map((area) => (
        <li key={area.name} className="text-ink-2">
          <span className="font-medium text-ink">{area.name}:</span> {area.doing}
        </li>
      ))}
    </ul>
  );
}
